import React, { useContext, useEffect, useRef, useState } from 'react';
import axios from "axios";
import { Link, navigate } from 'raviger';
import Moment from 'react-moment';
import { Carousel } from 'react-responsive-carousel';
import RangeSlider from 'react-bootstrap-range-slider';
import { Button, Card, Col, Form as BootstrapForm, ListGroup, Modal, OverlayTrigger, Row, Tooltip, Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBan, faGlobe, faMedkit, faCut, faEdit, faEnvelope, faLink, faMinusSquare, faTimes, faUserPlus, faFilePdf,
  faReply,
  faArrowAltCircleLeft
} from '@fortawesome/free-solid-svg-icons';
import { faHandsHolding, faUserDoctorMessage, faClawMarks, faFolderMedical, faHomeHeart, faPhonePlus, faRightLeft, faSplit, faSkullCrossbones } from '@fortawesome/pro-solid-svg-icons';
import 'react-responsive-carousel/lib/styles/carousel.min.css';
import MaterialRadio from '@mui/material/Radio';
import { makeStyles } from '@mui/styles';
import * as Yup from 'yup';
import { Formik } from "formik";
import Scrollbar from '../components/Scrollbars';
import { AnimalDeleteModal } from "../components/Modals";
import Header from '../components/Header';
import History from '../components/History';
import { DateTimePicker, DropDown, TextInput } from '../components/Form.js';
import { printAnimalCareSchedule } from './Utils';
import AnimalCoverImage from '../components/AnimalCoverImage';
import { SystemErrorContext } from '../components/SystemError';
import ShelterlyPrintifyButton from '../components/ShelterlyPrintifyButton';
import { useLocationWithRoutes } from '../hooks';
import LoadingLink from '../components/LoadingLink';
import { AuthContext } from "../accounts/AccountsReducer";
import ActionsDropdown from '../components/ActionsDropdown';
import { getFileNameFromUrl, isImageFile } from '../utils/files';
import { faClosedCaptioningSlash } from '@fortawesome/pro-regular-svg-icons';

const useStyles = makeStyles({
  root: {
    '&:hover': {
      backgroundColor: 'transparent',
    },
    color: 'white',
  },
  radio: {
    color: 'inherit',
  },
  icon: {
    borderRadius: 3,
    width: 16,
    height: 16,
    boxShadow: 'inset 0 0 0 1px rgba(16,22,26,.2), inset 0 -1px 0 rgba(16,22,26,.1)',
    backgroundColor: '#f5f8fa',
    backgroundImage: 'linear-gradient(180deg,hsla(0,0%,100%,.8),hsla(0,0%,100%,0))',
    '$root.Mui-focusVisible &': {
      outline: '2px auto rgba(19,124,189,.6)',
      outlineOffset: 2,
    },
    'input:hover ~ &': {
      backgroundColor: '#ebf1f5',
    },
    'input:disabled ~ &': {
      boxShadow: 'none',
      background: '#DFDDDD',
      cursor: 'not-allowed',
      pointerEvents: 'all !important',
      title: 'test'
    },
  },
  checkedIcon: {
    backgroundColor: '#137cbd',
    backgroundImage: 'linear-gradient(180deg,hsla(0,0%,100%,.1),hsla(0,0%,100%,0))',
    '&:before': {
      display: 'block',
      width: 16,
      height: 16,
      backgroundImage:
        "url(\"data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3E%3Cpath" +
        " fill-rule='evenodd' clip-rule='evenodd' d='M12 5c-.28 0-.53.11-.71.29L7 9.59l-2.29-2.3a1.003 " +
        "1.003 0 00-1.42 1.42l3 3c.18.18.43.29.71.29s.53-.11.71-.29l5-5A1.003 1.003 0 0012 5z' fill='%23fff'/%3E%3C/svg%3E\")",
      content: '""',
    },
    'input:hover ~ &': {
      backgroundColor: '#106ba3',
    },
  },
});

function PreRegAnimalDetails({ id, organization }) {
  const { state } = useContext(AuthContext);
  const { setShowSystemError } = useContext(SystemErrorContext);
  const [images, setImages] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const classes = useStyles();

  // Initial animal data.
  const [data, setData] = useState({
    id,
    owners: [],
    name: '',
    species_string: '',
    species_string_singular: '',
    sex: '',
    size: '',
    age: '',
    pcolor: '',
    scolor: '',
    color_notes: '',
    fixed: 'unknown',
    aggressive: 'unknown',
    aco_required: 'unknown',
    confined: 'unknown',
    injured: 'unknown',
    behavior_notes: '',
    medical_notes: '',
    microchip: '',
  });

  const [showSplit, setShowSplit] = useState(false);
  const handleCloseSplit = () => setShowSplit(false);
  const [species, setSpecies] = useState([]);

  // Hook for initializing data.
  useEffect(() => {
    let unmounted = false;
    let source = axios.CancelToken.source();
    setIsLoading(true);
    const fetchPreRegAnimalData = async () => {
      // Fetch Animal data.
      await axios.get('/animals/api/prereg/' + id + '/', {
        cancelToken: source.token,
        params: {
          organization: organization,
        },
      })
      .then(response => {
        if (!unmounted) {
          setData(response.data);
        }
      })
      .catch(error => {
        if (!unmounted) {
          setShowSystemError(true);
        }
      })
      .finally(() => setIsLoading(false));
    };
    fetchPreRegAnimalData();

    const fetchSpecies = () => {
      // Fetch Species data.
      axios.get('/animals/api/species/', {
        cancelToken: source.token,
      })
      .then(response => {
        if (!unmounted) {
          setSpecies(response.data);
        }
      })
      .catch(error => {
        if (!unmounted) {
          setShowSystemError(true);
        }
      });
    };
    fetchSpecies();

    // Cleanup.
    return () => {
      unmounted = true;
      source.cancel();
    };
  }, [id]);

  return (
    <>
    <Header><span id="back" style={{cursor:'pointer'}} onClick={(e) => {navigate("/" + organization + "/manage/owners/" + data.owners[0].id)}} className="me-3" title={"/" + organization + "/manage/owners/" + (data.owners.length ? data.owners[0].id : "")}><FontAwesomeIcon icon={faArrowAltCircleLeft} size="lg" inverse /></span>{`Preregistered Animal #${data.id || ' - '}`}</Header>
    <div className="row gx-3 me-0">
      <div className="col-6 d-flex" style={{marginRight:"0px"}}>
        <Card className="border rounded w-100">
          <Card.Body style={{marginTop:"-10px", marginBottom:"-6px"}}>
            <div className="d-flex justify-content-between">
              <h4 style={{paddingTop:"5px"}}>Information
              {data.confined === 'yes' ?
                <OverlayTrigger
                  key={"confined"}
                  placement="top"
                  overlay={
                    <Tooltip id={`tooltip-confined`}>
                      Animal is confined
                    </Tooltip>
                  }
                >
                  <FontAwesomeIcon icon={faLink} size="sm" className="ms-1 fa-move-up" transform={'shrink-2'} />
                </OverlayTrigger> :
              ""}
              {data.aggressive === 'yes' ?
                <OverlayTrigger
                  key={"aggressive"}
                  placement="top"
                  overlay={
                    <Tooltip id={`tooltip-aggressive`}>
                      Animal is aggressive
                    </Tooltip>
                  }
                >
                  <FontAwesomeIcon icon={faClawMarks} size="sm" className="ms-1" />
                </OverlayTrigger> :
              data.aggressive === 'no' ?
                <OverlayTrigger
                  key={"not-aggressive"}
                  placement="top"
                  overlay={
                    <Tooltip id={`tooltip-aggressive`}>
                      Animal is not aggressive
                    </Tooltip>
                  }
                >
                  <span className="fa-layers" style={{marginLeft:"2px"}}>
                    <FontAwesomeIcon icon={faClawMarks} size="sm" />
                    <FontAwesomeIcon icon={faBan} color="#ef5151" size="sm" transform={'shrink-2'} />
                  </span>
                </OverlayTrigger> :
              ""}
              {data.aco_required === 'yes' ?
                <OverlayTrigger
                  key={"aco-required"}
                  placement="top"
                  overlay={
                    <Tooltip id={`tooltip-aco-required`}>
                      Special Handling Required
                    </Tooltip>
                  }
                >
                  <FontAwesomeIcon icon={faHandsHolding} size="sm" className="ms-1" />
                </OverlayTrigger> : ""}
              {data.injured === 'yes' ?
                <OverlayTrigger
                  key={"injured"}
                  placement="top"
                  overlay={
                    <Tooltip id={`tooltip-injured`}>
                      Animal is injured
                    </Tooltip>
                  }
                >
                  <FontAwesomeIcon icon={faMedkit} size="sm" className="ms-1 fa-move-up" />
                </OverlayTrigger> :
              data.injured === 'no' ?
                <OverlayTrigger
                  key={"not-injured"}
                  placement="top"
                  overlay={
                    <Tooltip id={`tooltip-injured`}>
                      Animal is not injured
                    </Tooltip>
                  }
                >
                  <span className="fa-layers" style={{marginLeft:"2px"}}>
                    <FontAwesomeIcon icon={faMedkit} size="sm" className="fa-move-up" />
                    <FontAwesomeIcon icon={faBan} color="#ef5151" size="sm" transform={'shrink-2'} />
                  </span>
                </OverlayTrigger> :
              ""}
              {data.fixed === 'yes' ?
                <OverlayTrigger
                  key={"fixed"}
                  placement="top"
                  overlay={
                    <Tooltip id={`tooltip-fixed`}>
                      Animal is fixed or neutered
                    </Tooltip>
                  }
                >
                  <FontAwesomeIcon icon={faCut} size="sm" className="ms-1" />
                </OverlayTrigger> :
              data.fixed === 'no' ?
              <OverlayTrigger
                key={"not-fixed"}
                placement="top"
                overlay={
                  <Tooltip id={`tooltip-fixed`}>
                    Animal is not fixed or neutered
                  </Tooltip>
                }
              >
                <span className="fa-layers" style={{marginLeft:"2px"}}>
                  <FontAwesomeIcon icon={faCut} size="sm" />
                  <FontAwesomeIcon icon={faBan} color="#ef5151" size="sm" transform={'shrink-2'} />
                </span>
              </OverlayTrigger> :
              ""}
              {data.public === true ?
                <OverlayTrigger
                  key={"public"}
                  placement="top"
                  overlay={
                    <Tooltip id={`tooltip-public`}>
                      Animal is public
                    </Tooltip>
                  }
                >
                  <FontAwesomeIcon icon={faGlobe} size="sm" className="ms-1 fa-move-up" transform={'shrink-2'} />
                </OverlayTrigger> : ""}
                  {/* <OverlayTrigger
                    key={"not-public"}
                    placement="top"
                    overlay={
                      <Tooltip id={`tooltip-public`}>
                        Animal is not public
                      </Tooltip>
                    }
                  >
                    <span className="fa-layers" style={{marginLeft:"2px"}}>
                      <FontAwesomeIcon icon={faGlobe} size="sm" />
                      <FontAwesomeIcon icon={faBan} color="#ef5151" size="sm" transform={'shrink-2'} />
                    </span>
                  </OverlayTrigger>} */}
              </h4>
              {isLoading ? (
                <Spinner
                  className="align-self-center me-3"
                  {...{
                    as: 'span',
                    animation: 'border',
                    size: undefined,
                    role: 'status',
                    'aria-hidden': 'true',
                    variant: 'light',
                    style: {
                      height: '1.5rem',
                      width: '1.5rem',
                      marginBottom: '0.75rem'
                    }
                  }}
                />
              ) : (
                <ActionsDropdown variant="secondary" title="Actions">
                  <LoadingLink
                    href={"/" + organization + "/manage/animals/edit/" + id}
                    isLoading={isLoading}
                    className="d-block"
                    label="Update Animal"
                  >
                    <FontAwesomeIcon icon={faEdit} className="fas" inverse />
                  </LoadingLink>
                  <LoadingLink
                    label="Split Animal Group"
                    onClick={() => setShowSplit(true)}
                    isLoading={isLoading}
                    className="d-block"
                    disabled={data.animal_count < 2}
                    disabled_text="Individual animals may not be split."
                  >
                    <FontAwesomeIcon icon={faSplit} className="fas" style={{cursor:'pointer'}} inverse />
                  </LoadingLink>
                </ActionsDropdown>
              )}
            </div>
            <p className="phr pt-0 mt-1" />
            <ListGroup variant="flush" style={{marginTop:"-13px", marginBottom:"-13px"}}>
              <ListGroup.Item>
                <div className="row">
                  {data.animal_count > 1 ? <span className="col-6"><b>No. of Animals:</b> {data.animal_count}</span> : <span className="col-6"><b>Name:</b> {data.name||"Unknown Name"}</span>}
                  <span className="col-6" style={{textTransform:"capitalize"}}><b>Status:</b> {data.status}</span>
                </div>
              </ListGroup.Item>
              <ListGroup.Item style={{textTransform:"capitalize"}}>
                <div className="row">
                  <span className="col-6"><b>Species:</b> {data.species_string_singular}</span>
                  <span className="col-6"><b>Sex:</b> {data.sex||"Unknown"}</span>
                </div>
              </ListGroup.Item>
              <ListGroup.Item style={{textTransform:"capitalize"}}>
                <div className="row">
                  <span className="col-6"><b>Age:</b> {data.age||"Unknown"}</span>
                  <span className="col-6"><b>Size:</b> {data.size||"Unknown"}</span>
                </div>
              </ListGroup.Item>
              <ListGroup.Item style={{textTransform:"capitalize"}}>
                <div className="row">
                  <span className="col-6"><b>Primary Color:</b> {data.pcolor||"N/A"}</span>
                  <span className="col-6"><b>Secondary Color:</b> {data.scolor||"N/A"}</span>
                </div>
              </ListGroup.Item>
              <ListGroup.Item>
                <div className="row">
                  <span className="col-6"><b>Microchip:</b> {data.microchip || "N/A"}</span>
                  <span className="col-6"><b>External ID:</b> {data.external_id || "N/A"}</span>
                </div>
              </ListGroup.Item>
              {data.public && (
                <ListGroup.Item>
                  <b>Public Data:</b>{' '}
                  <Link
                    href={`/public?id=${data.id}`}
                    target="_blank"
                    className="text-link"
                    style={{ textDecoration: "none", color: "white" }}
                    rel="noopener noreferrer"
                  >
                    Public Record
                  </Link>
                  {data.pll_metadata?.imageSearch && (
                    <>
                      {' | '}
                      <Link
                        href={data.pll_metadata.imageSearch}
                        target="_blank"
                        className="text-link"
                        style={{ textDecoration: "none", color: "white" }}
                        rel="noopener noreferrer"
                      >
                        PLL Image Search
                      </Link>
                    </>
                  )}
                  {data.pll_metadata?.proximitySearch && (
                    <>
                      {' | '}
                      <Link
                        href={data.pll_metadata.proximitySearch}
                        target="_blank"
                        className="text-link"
                        style={{ textDecoration: "none", color: "white" }}
                        rel="noopener noreferrer"
                      >
                        PLL Proximity Search
                      </Link>
                    </>
                  )}
                </ListGroup.Item>
              )}
            </ListGroup>
          </Card.Body>
        </Card>
      </div>
      <div className="col-6 d-flex" style={{marginRight:"0px"}}>
        <Card className="border rounded w-100">
          <Card.Body style={{marginTop:"-10px", marginBottom:"-6px"}}>
            <div className="d-flex justify-content-between">
              <h4 style={{paddingTop:"5px"}}>Owners</h4>
            </div>
            <p className="phr pt-0 mt-1" />
            <Scrollbar no_shadow="true" style={{height:"210px"}} renderThumbHorizontal={props => <div {...props} style={{...props.style, display: 'none'}} />}>
              <ListGroup variant="flush" style={{marginTop:"-13px", marginBottom:"-13px"}}>
                {data.owners.map(owner => (
                  <ListGroup.Item key={owner.id}><b>Owner: </b><Link href={"/" + organization + "/manage/owners/" + owner.id} className="text-link" style={{textDecoration:"none", color:"white"}}>{owner.first_name} {owner.last_name}</Link>
                  {owner.display_phone ?
                    <span>&nbsp;{owner.display_phone}</span>
                    : ""}
                    {owner.email ?
                    <OverlayTrigger
                      key={"owner-email"}
                      placement="top"
                      overlay={
                        <Tooltip id={`tooltip-owner-email`}>
                          {owner.email}
                        </Tooltip>
                      }
                    >
                      <FontAwesomeIcon icon={faEnvelope} className="ms-1" inverse />
                    </OverlayTrigger>
                    : ""}
                  </ListGroup.Item>
                ))}
                </ListGroup>
              </Scrollbar>
            </Card.Body>
          </Card>
        </div>
    </div>
    <div className="col-12 d-flex mt-3 ps-0 pe-0">
      <Card className="border rounded" style={{width:"100%", height:"100%"}}>
        <Card.Body>
          <Card.Title>
            <h4>Description</h4>
          </Card.Title>
          <p className="phr"/>
          <ListGroup variant="flush" style={{marginTop:"-13px", marginBottom:"-13px"}}>
            <ListGroup.Item><b>Breed / Description:</b> {data.color_notes ? data.color_notes : 'N/A'}</ListGroup.Item>
            <ListGroup.Item style={{whiteSpace:"pre-line"}}><b>Animal Notes:</b> {data.behavior_notes ? data.behavior_notes : 'N/A'}</ListGroup.Item>
            <ListGroup.Item style={{whiteSpace:"pre-line"}}><b>Medical Notes:</b> {data.medical_notes ? data.medical_notes : 'N/A'}</ListGroup.Item>
          </ListGroup>
        </Card.Body>
      </Card>
    </div>
    <Formik
      initialValues={{'animal_count':data.animal_count, 'group_2':0}}
      enableReinitialize={true}
      validationSchema={Yup.object({
        animal_count: Yup.number(),
        group_2: Yup.number(),
      })}
      onSubmit={(values, { setSubmitting }) => {
        axios.patch('/animals/api/prereg/' + data.id + '/', values)
        .then(response => {
          setData(prevState => ({ ...prevState, animal_count:values.animal_count}));
          setShowSplit(false);
        })
        .catch(error => {
          setShowSystemError(true);
        });
      }}
    >
      {formikProps => (
      <Modal show={showSplit} onHide={handleCloseSplit}>
        <Modal.Header closeButton>
          <Modal.Title>Split Animal Group</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <span><b>Current Group: </b>{formikProps.values.animal_count} <span style={{textTransform:"capitalize"}}>{formikProps.values.animal_count === 1 && species.length > 0 && showSplit ? species.filter(specie_data => specie_data.plural_name.toLowerCase() === data.species_string.toLowerCase())[0].name : data.species_string}</span></span>
          <RangeSlider
            value={formikProps.values.group_2}
            onChange={(changeEvent) => {formikProps.setFieldValue("group_2", changeEvent.target.value);formikProps.setFieldValue("animal_count", data.animal_count - changeEvent.target.value);}}
            min={0}
            max={data.animal_count - 1}
            className="mb-3 mt-3"
          />
          <div className="mt-3"><b>New Group:&nbsp;</b>{formikProps.values.group_2}&nbsp;<span style={{textTransform:"capitalize"}}>{Number(formikProps.values.group_2) === 1 && species.length > 0 && data.id ? species.filter(specie_data => specie_data.plural_name.toLowerCase() === data.species_string.toLowerCase())[0].name : data.species_string}</span></div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={() => {formikProps.submitForm();}} disabled={Number(formikProps.values.group_2) === 0}>Save</Button>
          <Button variant="secondary" onClick={handleCloseSplit}>Close</Button>
        </Modal.Footer>
      </Modal>
      )}
    </Formik>
    </>
  );
};

export default PreRegAnimalDetails;
